import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { useState, useEffect } from "react";
import { Grid} from "@mui/material";

type ImageOption = {
  src: string | any;
  alt: string;
};

interface ISlideshowProps {
  images: ImageOption[];
  showCaption: boolean;
  showCount?: boolean;
  sx: any;
}

// Keyframes for fading animation
const fade = keyframes`
  from { opacity: .4 }
  to { opacity: 1 }
`;

// Styled components
const SlideshowContainer = styled.div`
  max-width: 1000px;
  position: relative;
  margin: auto;
`;

const Slide = styled.div`
  display: ${(props: { isActive: boolean }) =>
    props.isActive ? "block" : "none"};
  position: relative;
`;

const Text = styled.div`
  color: #f2f2f2;
  font-size: 15px;
  padding: 8px 12px;
  position: absolute;
  bottom: 8px;
  width: 100%;
  text-align: center;

  @media only screen and (max-width: 300px) {
    font-size: 11px;
  }
`;

const NumberText = styled.div`
  color: #f2f2f2;
  font-size: 12px;
  padding: 8px 12px;
  position: absolute;
  top: 0;
`;

const Dot = styled.span`
  height: 15px;
  width: 15px;
  margin: 0 2px;
  background-color: #bbb;
  border-radius: 50%;
  display: inline-block;
  transition: background-color 0.6s ease;

  &.active {
    background-color: #717171;
  }
`;

const Fade = styled.div`
  animation: ${fade} 1.5s;
`;

const Slideshow = ({ images, showCaption, sx, showCount }: ISlideshowProps) => {
  const [slideIndex, setSlideIndex] = useState(0);

  const imagesData = Array.isArray(images) ? images : [];
  const slides = [...imagesData];

  useEffect(() => {
    const timer = setInterval(() => {
      setSlideIndex((prevIndex) => (prevIndex + 1) % slides.length);
    }, 3000); // Change slide every 2 seconds

    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <Grid sx={{ ...sx }} container position={"relative"} width={"100%"}>
      <Grid item width={"100%"}>
        <SlideshowContainer>
          {slides.map((slide, index) => (
            <Slide
              key={index}
              isActive={slideIndex === index}
              style={{ position: "relative" }}
            >
              {slides.length > 1 && (showCount ?? true) &&(
                <NumberText
                  style={{
                    position: "absolute",
                    top: "2%",
                    left: "1.5rem",
                    backgroundColor: "#c3a01b",
                    zIndex: 2,
                    borderRadius: "0.5rem",
                    fontWeight: "bold",
                  }}
                >
                  {index + 1} / {slides.length}
                </NumberText>
              )}
              <Fade>
                <img
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "1rem",
                    objectFit: "contain",
                    aspectRatio: 16/9
                  }}
                  src={slide.src}
                  alt={`Slide ${index + 1}`}
                />
              </Fade>
              {showCaption && <Text>{slide.alt}</Text>}
            </Slide>
          ))}
        </SlideshowContainer>
      </Grid>
      {slides.length > 1 && (
        <Grid
          item
          textAlign={"center"}
          position={"absolute"}
          bottom={"8%"}
          width={"100%"}
          margin={"auto"}
          zIndex={2}
          alignItems={"center"}
        >
          {slides.map((_, index) => (
            <Dot key={index} className={slideIndex === index ? "active" : ""} />
          ))}
        </Grid>
      )}
    </Grid>
  );
};

export default Slideshow;
